/**  based and modified off of tmcw's excellent https://github.com/placemark/placemark 
 * 
 * @license MIT License

Copyright (c) 2023 Placemark

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

//import type { ShapefileGroup } from "app/lib/convert/shapefile";
import {remove}  from "lodash-es";
import { getExtension } from "src/components/CreateLayer/utils";

export type FileGroups = Array<FileGroup | ShapefileGroup>;

export interface ShapefileGroup {
    type: "shapefile";
    files: {
      shp: File;
      shx?: File;
      prj?: File;
      dbf?: File;
      cpg?: File;
    };
  }

export interface FileGroup {
  type: "file";
  file: File;
}

function asFileGroup(file: File): FileGroup {
  return { type: "file" as const, file };
}

export function getFileName(file: FileGroup | ShapefileGroup) {
  switch (file.type) {
    case "file":
      return file.file.name;
    case "shapefile":
      return file.files.shp.name;
  }
}

function asShapefileGroup(file: File): ShapefileGroup {
  return {
    type: "shapefile",
    files: {
      shp: file,
    },
  };
}

function isShp(file: File): boolean {
  return getExtension(file.name) === ".shp";
}

/**
 * Take unorganized files in a list of File objects
 * and attempt to group them into shapefile groups
 * and other freestanding files.
 */
export function groupFiles(files: readonly File[]): FileGroups {
  const pool = Array.from(files);

  const shapefileGroups = remove(pool, (file) => isShp(file)).map((file) =>
    asShapefileGroup(file)
  );

  function addGroup(group: ShapefileGroup, ext: keyof ShapefileGroup["files"]) {
    const base = group.files.shp.name.replace(/\.shp$/, "");
    const toAdd = remove(pool, (file) => file.name === `${base}.${ext}`);
    if (toAdd.length) {
      group.files[ext] = toAdd[0];
    }
  }

  for (const group of shapefileGroups) {
    addGroup(group, "shx");
    addGroup(group, "prj");
    addGroup(group, "dbf");
    addGroup(group, "cpg");
  }

  return [...pool.map((file) => asFileGroup(file)), ...shapefileGroups];
}
